import appConfig from '../../config/appConfig';
import storageService from '../storage/storageService';

const CLEANUP_STEP_TIMEOUT_MS = 500;
const CLEANUP_REQUEST_TIMEOUT_MS = 1000;
const REQUEST_TIMEOUT_MS = 5000;
const ENABLE_TIMEOUT_MS = 15000;
let sessionVersion = 0;
let logoutInProgress = false;
let enableInFlight = null;
let deviceClaim = null;
const retiredSubscriptions = new Set();

const cancelledError = () => new Error('Увімкнення push скасовано через зміну сесії.');
const timeoutError = () => new Error('Час очікування push-запиту вичерпано. Спробуйте ще раз.');
const subscriptionIdentity = (subscription) => JSON.stringify(subscription.toJSON());

// Race native promises too: abort must release the gate even when the underlying API cannot cancel.
const waitFor = (promise, signal) =>
    new Promise((resolve, reject) => {
        const aborted = () => reject(signal.reason || timeoutError());
        const finish = (callback) => (value) => {
            signal.removeEventListener('abort', aborted);
            callback(value);
        };

        Promise.resolve(promise).then(finish(resolve), finish(reject));
        if (signal.aborted) aborted();
        else signal.addEventListener('abort', aborted, {once: true});
    });

const withDeadline = async (action, timeout, parentSignal) => {
    const controller = new AbortController();
    const abort = () => controller.abort(parentSignal.reason || timeoutError());
    const timer = setTimeout(() => controller.abort(timeoutError()), timeout);

    if (parentSignal?.aborted) abort();
    else parentSignal?.addEventListener('abort', abort, {once: true});
    try {
        if (controller.signal.aborted) throw controller.signal.reason;
        return await waitFor(action(controller.signal), controller.signal);
    } finally {
        clearTimeout(timer);
        parentSignal?.removeEventListener('abort', abort);
    }
};

const getSupport = () => {
    const ios =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;

    if (ios && !standalone) return 'install-ios';
    return window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
        ? 'supported'
        : 'unsupported';
};

// The general request logger includes request bodies on failures. Keep subscription secrets out of it.
const request = async (
    path,
    method = 'GET',
    body,
    {token = storageService.getAccessTokenFromStorage(), signal, onLateResponse} = {}
) => {
    if (!token) throw new Error('Увійдіть до Energy Portal.');
    return withDeadline(
        async (requestSignal) => {
            const response = await fetch(`${appConfig.getApiUrl().replace(/\/$/, '')}/push/${path}`, {
                method,
                headers: {Authorization: `Bearer ${token}`, ...(body ? {'Content-Type': 'application/json'} : {})},
                body: body ? JSON.stringify(body) : undefined,
                cache: 'no-store',
                signal: requestSignal,
            });

            if (!response.ok) {
                if (response.status === 400) {
                    const error = await response.json().catch(() => null);
                    const validation = JSON.stringify(error);

                    if (validation.includes('Unsupported push provider endpoint.')) {
                        const host = body?.endpoint ? new URL(body.endpoint).hostname : '';

                        throw new Error(`Push-провайдер не підтримується: ${host}.`);
                    }
                    if (validation.includes('Invalid push subscription keys.')) {
                        throw new Error('Некоректні ключі push-підписки.');
                    }
                }
                if (response.status === 401) throw new Error('Сесія закінчилася. Увійдіть знову.');
                if (response.status === 503) throw new Error('Web Push не налаштовано на сервері.');
                throw new Error(`Не вдалося виконати push-запит (${response.status}).`);
            }
            const result = response.status === 204 ? undefined : await response.json();

            if (signal?.aborted && onLateResponse) await onLateResponse();
            return result;
        },
        REQUEST_TIMEOUT_MS,
        signal
    );
};

const register = async (signal) => {
    const registration = await waitFor(
        navigator.serviceWorker.register('/push-sw.js', {scope: '/', updateViaCache: 'none'}),
        signal
    );

    if (!registration.active) await waitFor(navigator.serviceWorker.ready, signal);
    return registration;
};

const hasCurrentKey = (subscription, key) => {
    const previousKey = subscription?.options?.applicationServerKey;

    if (!previousKey) return false;
    const bytes = ArrayBuffer.isView(previousKey)
        ? new Uint8Array(previousKey.buffer, previousKey.byteOffset, previousKey.byteLength)
        : new Uint8Array(previousKey);

    return bytes.length === key.length && bytes.every((byte, i) => byte === key[i]);
};

const toBytes = (value) => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');

    return Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')), (char) => char.charCodeAt(0));
};

// Native SW promises can hang too. Bound every cleanup step, not just fetch.
const bestEffort = async (action, timeout = CLEANUP_STEP_TIMEOUT_MS) => {
    const controller = new AbortController();
    let timer;

    try {
        return await Promise.race([
            Promise.resolve().then(() => action(controller.signal)),
            new Promise((resolve) => {
                timer = setTimeout(() => {
                    controller.abort();
                    resolve();
                }, timeout);
            }),
        ]);
    } catch (_) {
        // Failed cleanup must never prevent logout or the browser unsubscribe attempt.
    } finally {
        clearTimeout(timer);
    }
};

const removeSubscription = async (subscription, token, canUnsubscribe = () => true) => {
    if (token) {
        await bestEffort(
            (signal) => request('subscriptions', 'DELETE', {endpoint: subscription.endpoint}, {token, signal}),
            CLEANUP_REQUEST_TIMEOUT_MS
        );
    }
    if (canUnsubscribe()) {
        const removed = await bestEffort(() => subscription.unsubscribe());

        if (removed) retiredSubscriptions.delete(subscriptionIdentity(subscription));
    }
};

const retireSubscription = async (operation, subscription) => {
    const identity = subscriptionIdentity(subscription);
    const notReclaimed = () => !deviceClaim || deviceClaim.operation === operation || deviceClaim.identity !== identity;

    // A late native completion must not unsubscribe a device already claimed by a newer enable.
    if (notReclaimed()) await removeSubscription(subscription, operation.token, notReclaimed);
};

const cleanupForLogout = async () => {
    ++sessionVersion;
    logoutInProgress = true;
    const token = storageService.getAccessTokenFromStorage();
    const operation = enableInFlight;

    enableInFlight = null;
    if (operation) {
        if (operation.subscription) retiredSubscriptions.add(subscriptionIdentity(operation.subscription));
        operation.controller.abort(cancelledError());
    }

    try {
        // Prefer DELETE after an already-started POST. A late POST is also compensated by enable's catch.
        if (operation?.save) await bestEffort(() => operation.save);
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        const registration = await bestEffort(() => navigator.serviceWorker.getRegistration('/'));
        const subscription = registration && (await bestEffort(() => registration.pushManager.getSubscription()));

        if (subscription) {
            retiredSubscriptions.add(subscriptionIdentity(subscription));
            await removeSubscription(subscription, token);
        }
        deviceClaim = null;
    } finally {
        logoutInProgress = false;
    }
};

const getState = async () => {
    const support = getSupport();

    if (support !== 'supported') return {support, enabled: false};
    if (Notification.permission === 'denied') return {support, permission: 'denied', enabled: false};
    return withDeadline(async (signal) => {
        const registration = await register(signal);
        const subscription = await waitFor(registration.pushManager.getSubscription(), signal);
        const {publicKey} = await request('public-key', 'GET', undefined, {signal});
        const endpoints = subscription ? await request('subscriptions', 'GET', undefined, {signal}) : [];

        return {
            support,
            permission: Notification.permission,
            enabled:
                !!subscription &&
                hasCurrentKey(subscription, toBytes(publicKey)) &&
                endpoints.includes(subscription.endpoint),
        };
    }, ENABLE_TIMEOUT_MS);
};

// Call directly from click, before any network/SW await, to preserve the native iOS user gesture.
const enableForSession = async (operation) => {
    const isCurrentSession = () =>
        operation.version === sessionVersion &&
        !logoutInProgress &&
        !operation.controller.signal.aborted &&
        !!operation.login &&
        operation.login === storageService.getUserLoginFromStorage() &&
        !!storageService.getAccessTokenFromStorage();
    const checkSession = () => {
        if (!isCurrentSession()) throw operation.controller.signal.reason || cancelledError();
        // Refresh changes credentials, not identity. Never borrow credentials from the next account.
        operation.token = storageService.getAccessTokenFromStorage();
    };
    const sessionRequest = (path, method = 'GET', body, onLateResponse) => {
        checkSession();
        return request(path, method, body, {
            token: operation.token,
            signal: operation.controller.signal,
            onLateResponse,
        });
    };
    const acceptSubscription = async (promise) => {
        const tracked = promise.then(async (result) => {
            operation.subscription = result;
            if (!isCurrentSession() && result) await retireSubscription(operation, result);
            return result;
        });

        return waitFor(tracked, operation.controller.signal);
    };
    let subscription;

    try {
        checkSession();
        if (getSupport() !== 'supported') throw new Error('Push API недоступний.');
        const permission =
            Notification.permission === 'default'
                ? await waitFor(Notification.requestPermission(), operation.controller.signal)
                : Notification.permission;

        checkSession();
        if (permission !== 'granted') return {support: 'supported', permission, enabled: false};
        const registration = await register(operation.controller.signal);

        checkSession();
        const {publicKey} = await sessionRequest('public-key');

        checkSession();
        const key = toBytes(publicKey);

        subscription = await acceptSubscription(registration.pushManager.getSubscription());
        checkSession();
        const endpoints = subscription ? await sessionRequest('subscriptions') : [];

        if (
            subscription &&
            (!hasCurrentKey(subscription, key) ||
                !endpoints.includes(subscription.endpoint) ||
                retiredSubscriptions.has(subscriptionIdentity(subscription)))
        ) {
            await sessionRequest('subscriptions', 'DELETE', {endpoint: subscription.endpoint});
            checkSession();
            const removed = await waitFor(subscription.unsubscribe(), operation.controller.signal);

            if (!removed && (await waitFor(registration.pushManager.getSubscription(), operation.controller.signal))) {
                throw new Error('Не вдалося оновити push-підписку. Спробуйте ще раз.');
            }
            retiredSubscriptions.delete(subscriptionIdentity(subscription));
            subscription = null;
            operation.subscription = null;
            checkSession();
        }
        subscription =
            subscription ||
            (await acceptSubscription(
                registration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: key})
            ));
        checkSession();
        deviceClaim = {operation, identity: subscriptionIdentity(subscription)};
        operation.save = sessionRequest('subscriptions', 'POST', subscription.toJSON(), () =>
            retireSubscription(operation, subscription)
        ).then(async (result) => {
            if (!isCurrentSession()) await retireSubscription(operation, subscription);
            return result;
        });
        await operation.save;
        checkSession();
        return {support: 'supported', permission, enabled: true};
    } catch (error) {
        // Covers a native subscribe / server POST completing after logout's bounded cleanup.
        operation.controller.abort(error);
        if (subscription) {
            retiredSubscriptions.add(subscriptionIdentity(subscription));
            await retireSubscription(operation, subscription);
        }
        throw error;
    }
};

const enable = () => {
    if (logoutInProgress) return Promise.reject(new Error('Увімкнення push скасовано через зміну сесії.'));
    if (!enableInFlight) {
        const operation = {
            version: sessionVersion,
            login: storageService.getUserLoginFromStorage(),
            token: storageService.getAccessTokenFromStorage(),
            controller: new AbortController(),
        };
        const timer = setTimeout(() => operation.controller.abort(timeoutError()), ENABLE_TIMEOUT_MS);

        enableInFlight = operation;
        operation.promise = enableForSession(operation).finally(() => {
            clearTimeout(timer);
            if (enableInFlight === operation) enableInFlight = null;
        });
    }
    return enableInFlight.promise;
};

const disable = async () => {
    return withDeadline(async (signal) => {
        const registration = await register(signal);
        const subscription = await waitFor(registration.pushManager.getSubscription(), signal);

        if (subscription) {
            await request('subscriptions', 'DELETE', {endpoint: subscription.endpoint}, {signal});
            await waitFor(subscription.unsubscribe(), signal);
        }
        return {support: 'supported', permission: Notification.permission, enabled: false};
    }, ENABLE_TIMEOUT_MS);
};

export default {getState, enable, disable, cleanupForLogout};
